import { useState, useCallback } from "react";
import api from "../../services/api";
import { Publication, PublicationDetails, Payslip } from "../types/admin.types";

export interface UsePublicationsReturn {
  publications: Publication[];
  myPayslips: Payslip[];
  selectedPublication: PublicationDetails | null;
  showPublicationDetails: boolean;
  deletingPublicationId: string | null;
  setShowPublicationDetails: (v: boolean) => void;
  loadPublications: () => Promise<void>;
  loadMyPayslips: () => Promise<void>;
  handleViewPublication: (id: string) => Promise<void>;
  handleDeletePublication: (id: string) => Promise<void>;
  handleDownloadMyPayslip: (
    payslipId: number,
    fileName: string,
  ) => Promise<void>;
}

const POLL_INTERVAL_MS = 2500;

export const usePublications = (
  setError: (msg: string) => void,
  setSuccess: (msg: string) => void,
): UsePublicationsReturn => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [myPayslips, setMyPayslips] = useState<Payslip[]>([]);
  const [selectedPublication, setSelectedPublication] =
    useState<PublicationDetails | null>(null);
  const [showPublicationDetails, setShowPublicationDetails] = useState(false);
  // ID of the publication currently being deleted (drives spinner in the table row)
  const [deletingPublicationId, setDeletingPublicationId] = useState<
    string | null
  >(null);

  const loadPublications = useCallback(async () => {
    try {
      const r = await api.get("/payslips/publications");
      setPublications(r.data.publications);
    } catch {
      setError("Failed to load publications");
    }
  }, [setError]);

  const loadMyPayslips = useCallback(async () => {
    try {
      const r = await api.get("/payslips/my-payslips");
      setMyPayslips(r.data.payslips);
    } catch {}
  }, []);

  const handleViewPublication = async (id: string) => {
    try {
      const r = await api.get(`/payslips/publications/${id}`);
      setSelectedPublication(r.data);
      setShowPublicationDetails(true);
    } catch {
      setError("Failed to load publication details");
    }
  };

  const pollDeleteJob = async (jobId: string): Promise<void> => {
    const attempt = async (): Promise<void> => {
      try {
        const r = await api.get(`/payslips/delete-jobs/${jobId}`);
        const { status, result, error } = r.data;

        if (status === "done") {
          setSuccess(result?.message || "Publication deleted successfully");
          setDeletingPublicationId(null);
          await loadPublications();
        } else if (status === "failed") {
          setError(error || "Deletion failed");
          setDeletingPublicationId(null);
        } else {
          await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
          await attempt();
        }
      } catch {
        // Network hiccup — one retry then give up
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS * 2));
        try {
          const r = await api.get(`/payslips/delete-jobs/${jobId}`);
          const { status, result, error } = r.data;
          if (status === "done") {
            setSuccess(result?.message || "Publication deleted successfully");
            await loadPublications();
          } else if (status === "failed") {
            setError(error || "Deletion failed");
          } else {
            setError("Could not confirm deletion status. Please refresh.");
          }
        } catch {
          setError("Could not confirm deletion status. Please refresh.");
        }
        setDeletingPublicationId(null);
      }
    };

    await attempt();
  };

  const handleDeletePublication = async (id: string) => {
    if (
      !window.confirm(
        "Delete all payslips from this publication? This cannot be undone.",
      )
    )
      return;

    setDeletingPublicationId(id);

    try {
      const r = await api.delete(`/payslips/publications/${id}`);
      const { jobId } = r.data;
      await pollDeleteJob(jobId);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to start deletion");
      setDeletingPublicationId(null);
    }
  };

  const handleDownloadMyPayslip = async (
    payslipId: number,
    fileName: string,
  ) => {
    try {
      const r = await api.get(`/payslips/download/${payslipId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess("Payslip downloaded successfully");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to download payslip");
    }
  };

  return {
    publications,
    myPayslips,
    selectedPublication,
    showPublicationDetails,
    setShowPublicationDetails,
    deletingPublicationId,
    loadPublications,
    loadMyPayslips,
    handleViewPublication,
    handleDeletePublication,
    handleDownloadMyPayslip,
  };
};
